import { useState, useCallback, useMemo, useEffect } from 'react';
import { syncIdentityKeysFromToken, tokenService } from './tokenService.js';
import { decodeJwtPayload } from './jwtUtils.js';

/**
 * The backend JWT's own `user_uuid` claim is the source of truth — not the
 * separate `user_uuid` localStorage key. A cross-subdomain workspace handoff
 * (tokenHandoff.js's consumeHandoff -> tokenService.setToken) only ever
 * stores the JWT itself; it never writes that separate key. Landing on a
 * workspace subdomain that way left `userId` empty forever (e.g. Agents'
 * create-agent flow saw '' and refused to create), even though a perfectly
 * valid token — and its user_uuid claim — was sitting right there. Falling
 * back to the localStorage key only covers a legacy/edge case where the
 * token is momentarily absent but the key still is.
 */
const deriveUserId = () =>
  decodeJwtPayload(tokenService.getToken())?.user_uuid ||
  localStorage.getItem('user_uuid') ||
  '';

const useAuthState = () => {
  const [userId, setUserId] = useState(deriveUserId);
  const [selectedAccount, setSelectedAccount] = useState(null);

  const syncWorkspaceUuidFromToken = useCallback((token) => {
    const decodedToken = decodeJwtPayload(token);
    if (decodedToken?.tenant_uuid) {
      localStorage.setItem('workspace_uuid', decodedToken.tenant_uuid);
      setSelectedAccount((prev) => ({
        ...(prev || {}),
        tenantId: decodedToken.tenant_uuid,
      }));
    }
  }, []);

  // Note: deliberately no try/catch here — a failed exchange (e.g. a stale
  // localStorage `workspace_uuid` the real backend no longer recognises)
  // must propagate up to getGlobalInformation/Auth0AuthBridge so the app
  // can clear state and bounce back to login, instead of silently doing
  // nothing and leaving ProtectedRoute stuck on "Authenticating…" forever.
  const fetchApiToken = useCallback(
    async (options = {}) => {
      const { auth0AccessToken } = options;

      if (auth0AccessToken) {
        const token = await tokenService.fetchTokenWithAuth0(auth0AccessToken);
        if (token) {
          const decodedToken = decodeJwtPayload(token);
          const newUserId =
            decodedToken?.user_uuid || localStorage.getItem('user_uuid') || '';
          // Set explicitly rather than relying solely on the onTokenChange
          // listener below: it's technically redundant once that listener
          // fires (tokenService.setToken triggers it), but keeping this
          // write here makes the update synchronous with the rest of this
          // function instead of one tick later, and costs nothing since
          // both writes settle to the same value.
          setUserId(newUserId);
          syncWorkspaceUuidFromToken(token);
          localStorage.setItem('user_uuid', decodedToken?.user_uuid || newUserId);
          // Whether the browser needs to land on {slug}.{rootDomain}, or
          // show the workspace picker (multiple workspaces, ambiguous
          // choice), is decided by WorkspaceGate (apps/core-shell) once
          // this token is in place — not here. This function's only job
          // is obtaining and storing a valid backend JWT.
        }
        return;
      }

      const userUuid = localStorage.getItem('user_uuid');
      const tenantUuid = localStorage.getItem('workspace_uuid');

      if (!userUuid || !tenantUuid) {
        throw new Error('[useAuthState] Could not get user_uuid or tenant_uuid');
      }

      // Same reasoning as above — redundant with the onTokenChange listener,
      // kept for a synchronous update.
      setUserId(userUuid);

      const token = await tokenService.fetchNewToken(userUuid, tenantUuid);
      if (token) {
        const decodedToken = decodeJwtPayload(token);
        syncWorkspaceUuidFromToken(token);
        localStorage.setItem('user_uuid', decodedToken?.user_uuid || userUuid);
      }
    },
    [syncWorkspaceUuidFromToken],
  );

  const clear = useCallback(() => {
    localStorage.removeItem('api_access_token');
    localStorage.removeItem('user_uuid');
    localStorage.removeItem('workspace_uuid');
    setUserId('');
    setSelectedAccount(null);
  }, []);

  const getGlobalInformation = useCallback(
    async (options = {}) => {
      try {
        await fetchApiToken(options);
      } catch (error) {
        console.error('[useAuthState] Error in getGlobalInformation:', error);
        clear();
        // Rethrow so Auth0AuthBridge's own catch runs clearAuthAndRedirect()
        // — a failed exchange must bounce back to a fresh login, not leave
        // ProtectedRoute waiting forever for a token that will never arrive.
        throw error;
      }
    },
    [fetchApiToken, clear],
  );

  // Re-derive once on mount, THEN subscribe to future token changes.
  //
  // The mount-time re-derive is not redundant with the lazy useState above:
  // this hook's parent (AppAuthProviderWrapper) renders — and so calls
  // useState(deriveUserId) — BEFORE its child Auth0AuthBridge renders and
  // runs consumeHandoff() (via that component's own lazy useState). On a
  // fresh handoff landing, deriveUserId() at this hook's own mount therefore
  // sees no token yet. And subscribing here is too late to catch that same
  // consumeHandoff call either: it runs during the render phase, before any
  // effect from this mount has been committed, so the onTokenChange listener
  // isn't registered yet when tokenService.setToken() fires for it. Only
  // re-deriving again here, after commit — by which point consumeHandoff has
  // already run — actually picks up the token. (Found by Bugbot on the PR
  // for CT-5288 — an earlier revision dropped this call as a supposedly-
  // redundant micro-optimization, which silently reintroduced the exact bug
  // being fixed for a plain production mount, no StrictMode double-render to
  // paper over it.)
  //
  // The same pass also mirrors the token's claims into the `user_uuid` /
  // `workspace_uuid` localStorage keys. `consumeHandoff` writes them for every
  // NEW handoff landing, but that alone would leave two cases broken: a tab
  // that landed before this shipped (its fragment is long gone, so
  // `consumeHandoff` returns null forever), and a renewal that moved the user
  // to a different tenant. This effect runs on every mount and on every token
  // write, so both heal on the next render instead of waiting for a re-login.
  useEffect(() => {
    const syncFromToken = () => {
      const token = tokenService.getToken();
      if (token) syncIdentityKeysFromToken(token);
      const derived = deriveUserId();
      setUserId((prev) => (prev === derived ? prev : derived));
    };
    syncFromToken();
    return tokenService.onTokenChange(syncFromToken);
  }, []);

  return useMemo(
    () => ({
      userId,
      selectedAccount,
      getGlobalInformation,
      clear,
    }),
    [userId, selectedAccount, getGlobalInformation, clear],
  );
};

export default useAuthState;
